import React, { useEffect, useRef } from 'react';
import {
  PostHogErrorBoundary as PostHogReactErrorBoundary,
  usePostHog,
} from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { getLastApiRequestId } from '../../services/api';
import { reportReactBoundaryException } from '../../utils/exceptionCapture';

interface ErrorFallbackProps {
  error: Error;
  componentStack: string;
  exceptionEvent: unknown;
}

function generateIncidentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `inc-${Date.now()}`;
}

const ErrorFallbackContent: React.FC<ErrorFallbackProps> = ({
  error,
  componentStack,
  exceptionEvent,
}) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const incidentIdRef = useRef<string | null>(null);
  if (incidentIdRef.current === null) {
    incidentIdRef.current = generateIncidentId();
  }
  const incidentId = incidentIdRef.current;
  const sessionId =
    posthog && typeof posthog.get_session_id === 'function'
      ? posthog.get_session_id() ?? null
      : null;
  const lastRequestId = getLastApiRequestId();

  useEffect(() => {
    if (!posthog) {
      return;
    }
    reportReactBoundaryException(posthog, error, incidentId);
    posthog.capture('error_boundary_shown', {
      incident_id: incidentId,
      session_id: sessionId,
      last_api_request_id: lastRequestId,
      error_message: error.message,
      component_stack_preview: (componentStack || '').slice(0, 500),
    });
  }, [posthog, incidentId, sessionId, lastRequestId, error, componentStack]);

  console.error('React Error Boundary triggered:', {
    error: error.message,
    stack: error.stack,
    componentStack,
    exceptionEvent,
    incident_id: incidentId,
    session_id: sessionId,
    last_api_request_id: lastRequestId,
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>

          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
            {t('error_occurred', { ns: 'common' })}
          </h1>

          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {t('error_unexpected_message', { ns: 'common' })}
          </p>

          <div className="mb-6 p-3 rounded-lg bg-slate-100 dark:bg-slate-900/50 text-left text-xs font-mono text-slate-700 dark:text-slate-300 space-y-1 break-all">
            <div>
              <span className="font-semibold text-slate-500 dark:text-slate-400">
                {t('error_support_reference', { ns: 'common' })}:
              </span>{' '}
              {incidentId}
            </div>
            {sessionId ? (
              <div>
                <span className="font-semibold text-slate-500 dark:text-slate-400">
                  {t('error_analytics_session', { ns: 'common' })}:
                </span>{' '}
                {sessionId}
              </div>
            ) : null}
            {lastRequestId ? (
              <div>
                <span className="font-semibold text-slate-500 dark:text-slate-400">
                  {t('error_last_api_request', { ns: 'common' })}:
                </span>{' '}
                {lastRequestId}
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {t('reload_page', { ns: 'common' })}
            </button>

            <button
              type="button"
              onClick={() => (window.location.href = '/')}
              className="w-full bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {t('go_home', { ns: 'common' })}
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300">
                {t('show_technical_details', { ns: 'common' })}
              </summary>
              <div className="mt-3 p-3 bg-slate-100 dark:bg-slate-900 rounded text-xs font-mono text-slate-800 dark:text-slate-200 overflow-auto max-h-40">
                <div className="mb-2">
                  <strong>Error:</strong> {error.message}
                </div>
                {error.stack && (
                  <div className="mb-2">
                    <strong>Stack:</strong>
                    <pre className="whitespace-pre-wrap mt-1">{error.stack}</pre>
                  </div>
                )}
                {componentStack && (
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="whitespace-pre-wrap mt-1">{componentStack}</pre>
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

interface PostHogErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps> | React.ReactNode;
}

const PostHogErrorFallbackWrapper: React.FC<any> = (props: any) => {
  const error =
    props.error instanceof Error
      ? props.error
      : new Error(props.error ? String(props.error) : 'Unknown error');

  return (
    <ErrorFallbackContent
      error={error}
      componentStack={props.componentStack || ''}
      exceptionEvent={props.exceptionEvent}
    />
  );
};

const PostHogErrorBoundary: React.FC<PostHogErrorBoundaryProps> = ({
  children,
  fallback,
}) => {
  if (React.isValidElement(fallback) || (typeof fallback !== 'function' && fallback !== undefined)) {
    return (
      <PostHogReactErrorBoundary fallback={fallback as React.ReactNode}>
        {children}
      </PostHogReactErrorBoundary>
    );
  }

  const fallbackComponent = (fallback || PostHogErrorFallbackWrapper) as any;

  return (
    <PostHogReactErrorBoundary fallback={fallbackComponent}>
      {children}
    </PostHogReactErrorBoundary>
  );
};

export default PostHogErrorBoundary;
